/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import urlJoin from 'proper-url-join';

import type { Endpoints, WindowConversation, WindowWxo } from '../../../ts';
import { DeployEnv, WxoExperience } from '../../../ts';
import type { FeatureFlags, LanguageFeatures } from '../../../ts/feature-flags';
import { MfePrefix } from '../../../ts/mfe';
import { Language } from '../../../ts/skill';
import { envLanguageToHumanReadable } from './string';

export let config: WindowConversation = {} as WindowConversation,
  baseUrl: WindowConversation['baseUrl'],
  endpoints: WindowConversation['endpoints'],
  cloudConsoleUrl: WindowConversation['cloudConsoleUrl'],
  features: WindowConversation['features'],
  languages: WindowConversation['languages'],
  deployEnv: WindowConversation['deployEnv'],
  segment: WindowConversation['segment'],
  webExperience: WindowConversation['webExperience'],
  webSupport: WindowConversation['webSupport'],
  integrations: WindowConversation['integrations'],
  plusTrialLength: WindowConversation['plusTrialLength'],
  activityMonitor: WindowConversation['activityMonitor'],
  contentHubBaseUrl: string,
  isIbmCloud: WindowConversation['isIbmCloud'],
  wxoUrl: WindowConversation['wxoUrl'],
  // Returns true if Tooling is executing within an AWS deployment
  isAws: WindowConversation['isAws'],
  // Returns true if Tooling is integrated with watsonx Orchestrate.
  // This will limit the feature set the user will have access to, hide
  // UI elements as needed, and show a different product name
  isWxoEnabled: WindowConversation['isWxoEnabled'],
  isAgentBuilder: boolean,
  // Returns true if Tooling is running in a CP4D environment
  isCpd: WindowConversation['isCpd'],
  isSlotFillingEnabled: WindowConversation['isSlotFillingEnabled'],
  cpdPrefix: WindowConversation['cpdPrefix'];

export const defaultLanguage = Language.English;

export const prodEnvs = Object.freeze([
  DeployEnv.PRODUCTION,
  DeployEnv.PREPRD,
  DeployEnv.FRAPRD,
  DeployEnv.LONPRD,
  DeployEnv.SYDPRD,
  DeployEnv.TOKPRD,
  DeployEnv.WDCPRD
]);

export const devEnvs = Object.freeze([
  DeployEnv.DEV,
  DeployEnv.STAGE,
  DeployEnv.DEVAWS,
  DeployEnv.STAGEAWS
]);

export const isDeployProduction = (env: DeployEnv): boolean =>
  prodEnvs.includes(env);

export const isDeployDevelopment = (env: DeployEnv): boolean =>
  devEnvs.includes(env);

interface LanguageItem {
  key: Language;
  label: string;
}

export const initializeConfig = (): void => {
  config = _.get(window, 'conversation.config', {}) as WindowConversation;

  baseUrl = config.baseUrl;

  endpoints = config.endpoints || ({} as Endpoints);

  cloudConsoleUrl = config.cloudConsoleUrl;

  features = config.features || ({} as FeatureFlags);

  languages = config.languages || ([] as LanguageFeatures[]);

  deployEnv = config.deployEnv;

  segment = config.segment;

  // If the Segment script source is a relative path (which happens only in
  // development), prepend the URL prefix (if specified) to the src path.
  // This will allow the script to load as expected even if Tooling is being
  // used as a Micro Front-End (MFE).
  if (segment?.src?.startsWith('/')) {
    segment.src = urlJoin(
      config.isWxoEnabled ? MfePrefix.ASSISTANTS : undefined,
      segment.src
    );
  }

  webExperience = config.webExperience;

  webSupport = config.webSupport || ({} as WindowConversation['webSupport']);

  integrations =
    config.integrations || ({} as WindowConversation['integrations']);

  plusTrialLength = config.plusTrialLength;

  activityMonitor = config.activityMonitor;

  // Content hub is a static site hosted on ibm.com
  // When in WA dev and staging environment, point to content hub staging url
  // When in WA prod environment, point to content hub prod url
  const hostPrefix =
    config.deployEnv === DeployEnv.STAGE || config.deployEnv === DeployEnv.DEV
      ? 'wwwstage'
      : 'www';
  contentHubBaseUrl = `https://${hostPrefix}.ibm.com/products/watson-assistant/resources/learning`;

  // If WXO is enabled, it means Tooling is running as an MFE; if this is the
  // case, whenever we need to send the user back to WXO, we simple reference
  // the origin of the current page, which should be the root of the WXO page.
  // Note that we can NOT in these cases use document.baseURI because that
  // would contain the path element where WXO loads Tooling (e.g. "/assistants"),
  // whereas the WXO URL needs to be the root URL for the WXO page.
  // In CPD we know we must append the well-known application prefix for WXO.
  if (config.isWxoEnabled) {
    wxoUrl = config.isCpd
      ? urlJoin(window.location.origin, 'orchestrate')
      : window.location.origin;
  }

  isAws = config.isAws;

  isIbmCloud = config.isIbmCloud;

  isWxoEnabled = config.isWxoEnabled;

  isAgentBuilder =
    isWxoEnabled &&
    (_.get(window, 'WO_Meta', {}) as WindowWxo).chatVersion ===
      WxoExperience.AGENT_BUILDER;

  isCpd = config.isCpd;

  isSlotFillingEnabled = config.isSlotFillingEnabled;

  cpdPrefix = config.cpdPrefix;
};

// On module load, initialize the config variables.
initializeConfig();

//TODO this is adapted from newSkillForm - refactor to common copy?
export const getLanguagesDropdownItems = (): {
  key: Language;
  label: string;
}[] => {
  const configLanguages: LanguageFeatures[] = config?.languages ?? [];

  const agnosticLang: LanguageFeatures | undefined = configLanguages.find(
    v => v.value === 'xx'
  );

  let languages: LanguageItem[] = configLanguages.reduce(
    (acc: LanguageItem[], currLang: LanguageFeatures) => {
      if (currLang.value !== 'xx') {
        acc.push({
          key: currLang.value as Language,
          label: envLanguageToHumanReadable({
            beta: currLang.experimental,
            label: currLang.label,
            value: currLang.value
          })
        });
      }
      return acc;
    },
    []
  );
  languages = _.sortBy(languages, 'label');

  if (agnosticLang) {
    languages.push({
      key: agnosticLang.value as Language,
      label: envLanguageToHumanReadable({
        beta: agnosticLang.experimental,
        label: agnosticLang.label,
        value: agnosticLang.value
      })
    });
  }
  languages =
    languages.length !== 0
      ? languages
      : [{ key: Language.English, label: 'English (US)' }];
  return languages;
};

export default config;
